import React from 'react';
import Dropzone from 'react-dropzone';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';

/* The option lists in util/options use the Semantic shape */
export interface Option {
    key: string | number;
    value: any;
    text: string;
}

/* Two columns of fields on desktop, one on mobile */
export const FormGrid: React.FC<{ isMobile?: boolean }> = ({ isMobile = false, children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>{children}</Box>
);

/* The theme paints every helper text red; only errors should be */
const helperSx = (error?: boolean) => ({ sx: { color: error ? 'error.main' : 'text.secondary' } });

type OptionSelectProps = Omit<TextFieldProps, 'select' | 'onChange'> & {
    options: Option[];
    onValueChange: (value: any) => void;
};

/* Short fixed lists (gender, source, severity) */
export const OptionSelect: React.FC<OptionSelectProps> = ({ options, onValueChange, value, error, ...props }) => (
    <TextField
        {...props}
        select
        value={value ?? ''}
        error={error}
        onChange={(event) => onValueChange(event.target.value)}
        FormHelperTextProps={helperSx(error)}
        fullWidth
    >
        {options.map((option) => (
            <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>
        ))}
    </TextField>
);

// Long lists match from the start of the name, like the old Semantic searchFromStart
const startsWithFilter = createFilterOptions<Option>({ matchFrom: 'start', stringify: (option) => option.text });

interface OptionAutocompleteProps {
    label: string;
    name?: string;
    options: Option[];
    value: any;
    onValueChange: (value: any) => void;
    onBlur?: () => void;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
}

/* Long searchable lists (countries, languages) */
export const OptionAutocomplete: React.FC<OptionAutocompleteProps> = ({ label, name, options, value, onValueChange, onBlur, disabled, required, error, helperText }) => {
    // Catalogue lists can repeat an entry (e.g. a brand listed twice); keep the first of each value
    const unique = options.filter((option, i) => options.findIndex((other) => other.value === option.value) === i);

    return (
    <Autocomplete
        options={unique}
        value={unique.find((option) => option.value === value) || null}
        onChange={(_, option) => onValueChange(option ? option.value : null)}
        getOptionLabel={(option) => option.text}
        renderOption={(props, option) => (
            <li {...props} key={option.key}>
                {option.text}
            </li>
        )}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        filterOptions={startsWithFilter}
        onBlur={onBlur}
        disabled={disabled}
        renderInput={(params) => (
            <TextField {...params} name={name} label={label} required={required} error={error} helperText={helperText} FormHelperTextProps={helperSx(error)} />
        )}
    />
    );
};

interface AvatarPickerProps {
    src: string;
    error?: string;
    hint?: string;
    onPick: (file: File) => void;
    onReject: () => void;
}

/* Avatar with a "Change photo" button (jpeg/png up to 1 MB) */
export const AvatarPicker: React.FC<AvatarPickerProps> = ({ src, error, hint, onPick, onReject }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
        <Avatar src={src} alt="avatar" sx={{ width: 88, height: 88 }} />
        <Box>
            <Dropzone
                multiple={false}
                maxSize={1000000}
                accept="image/jpeg, image/png"
                onDropAccepted={([file]) => onPick(file)}
                onDropRejected={onReject}
            >
                {({ getRootProps, getInputProps }) => (
                    <Box {...getRootProps()} sx={{ display: 'inline-block' }}>
                        <input {...getInputProps()} />
                        <Tooltip title={hint || ''}>
                            <Button variant="outlined" startIcon={<PhotoCameraOutlinedIcon />}>Change photo</Button>
                        </Tooltip>
                    </Box>
                )}
            </Dropzone>
            <Typography sx={{ fontSize: 12, mt: 0.75 }} color={error ? 'error' : 'textSecondary'}>
                {error || 'JPEG or PNG, up to 1 MB'}
            </Typography>
        </Box>
    </Box>
);
